import {
  FormFillingProps,
  GinStepsProvider,
} from '@hellodarwin/app-features/lib/features/gin-steps/gin-steps-provider';
import StepsManagerModal from '@hellodarwin/app-features/lib/features/steps-manager/modal';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import { ProgramGrantStatus } from '@hellodarwin/core/lib/features/entities';
import { AssetDownloadResponse } from '@hellodarwin/core/lib/features/entities/assets-entities';
import { isGinStepAssignedToContact } from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import dollarFormat from '@hellodarwin/core/lib/features/helpers/dollar-format';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../../../app/app-hooks';
import { downloadAsset } from '../../../../../features/api/slices/assets-slice';
import {
  selectGinStepForm,
  selectGinStepsIsLoading,
  selectItemIdsByStepId,
  selectItemsById,
  selectPagesById,
  selectStepIdsByPageId,
  selectStepsById,
  selectStepsForCustomClient,
} from '../../../../../features/api/slices/gin-steps-slice';
import {
  fetchAdmin,
  selectAdminByAdminId,
} from '../../../../../features/api/slices/global-slice';
import {
  fetchCompanyUsers,
  selectCompany,
  selectContact,
} from '../../../../../features/api/slices/profile-slice';
import {
  fetchGrant,
  selectGrantById,
  selectProgramById,
} from '../../../../../features/api/slices/programs-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../../../../../features/api/use-client-api';
import useFormFillingGinRichTextParseOptions from '../gin-steps/use-form-filling-gin-rich-text-parse-options';
import useGinStepUtils from '../gin-steps/use-gin-step-utils';
import GinStepSteps from './gin-step-steps';
import { GinStepsCustomClientState } from './styles';

interface GinStepsCustomClientProps {
  programId: string;
}

const excludedStatuses = [
  ProgramGrantStatus.Submitted,
  ProgramGrantStatus.Accepted,
  ProgramGrantStatus.Refused,
  ProgramGrantStatus.ClosedLost,
];

const GinStepsCustomClient = ({ programId }: GinStepsCustomClientProps) => {
  const { selectedLocale } = useLocale();
  const dispatch = useAppDispatch();
  const api = useNewClientApi();
  const oldApi = useClientApi();

  const theme = useTheme();
  const { getRichTextParseOptions, contextHolder } =
    useFormFillingGinRichTextParseOptions(programId);

  // Selectors
  const isLoading = useAppSelector(selectGinStepsIsLoading);
  const ginForm = useAppSelector(selectGinStepForm);
  const stepsById = useAppSelector(selectStepsById);
  const pagesById = useAppSelector(selectPagesById);
  const itemsById = useAppSelector(selectItemsById);
  const stepsForCustomClient = useAppSelector(selectStepsForCustomClient);
  const stepIdsByPageId = useAppSelector(selectStepIdsByPageId);
  const itemIdsByStepId = useAppSelector(selectItemIdsByStepId);
  const program = useAppSelector((state) =>
    selectProgramById(state, programId || ''),
  );

  const company = useAppSelector(selectCompany);
  const contact = useAppSelector(selectContact);

  const { t } = useTranslation();

  const currentStepAdmin = useAppSelector((state) =>
    selectAdminByAdminId(
      state,
      ginForm?.current_step?.assigned_admin || company.account_manager || '',
    ),
  );
  const currentGrant = useAppSelector((state) =>
    selectGrantById(state, ginForm?.grant_id || ''),
  );
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);
  const {
    assetApplicationProps,
    propertyItemProps,
    handleSubmitApplicationItem,
    handleDeleteApplicationItem,
    applicationItemStatesById,
    applicationStatusModal,
    closeApplicationStatusModal,
    triggerProgramStatusDialog,
    handleUpdateStepValidation,
  } = useGinStepUtils({
    programId,
  });

  const handleDownloadAsset: (
    assetId: string,
    name?: string,
  ) => Promise<AssetDownloadResponse> = (assetId: string, name?: string) => {
    return dispatch(
      downloadAsset({ api, asset_id: assetId, nameFromItem: name }),
    ).unwrap();
  };

  useEffect(() => {
    dispatch(fetchCompanyUsers({ api: oldApi }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!!ginForm?.current_step?.assigned_admin) {
      dispatch(
        fetchAdmin({
          api: oldApi,
          adminId: ginForm?.current_step?.assigned_admin || '',
        }),
      );
      dispatch(
        fetchGrant({
          api: oldApi,
          grantId: ginForm?.grant_id || '',
          locale: selectedLocale,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ginForm]);

  const formFillingProps: FormFillingProps | undefined = useMemo(() => {
    if (
      !assetApplicationProps ||
      !propertyItemProps ||
      !applicationItemStatesById
    )
      return undefined;
    return {
      applicationItemStatesById,
      handleSubmitApplicationItem,
      handleDeleteApplicationItem,
      triggerProgramStatusDialog,
      assetApplicationProps,
      propertyItemProps,
      handleUpdateStepValidation,
    } satisfies FormFillingProps;
  }, [
    applicationItemStatesById,
    handleSubmitApplicationItem,
    handleDeleteApplicationItem,
    triggerProgramStatusDialog,
    assetApplicationProps,
    propertyItemProps,
    handleUpdateStepValidation,
  ]);

  const getCurrentStepIndex = () => {
    var stepCounter = 1;
    var foundIncompleteStep = 1;
    ginForm?.pages.forEach((page) =>
      page.steps?.forEach((step) => {
        if (step.step_id === ginForm?.current_step?.step_id) {
          foundIncompleteStep = stepCounter;
        }
        stepCounter++;
      }),
    );
    return foundIncompleteStep;
  };

  const customClientHasTasksNotCompleted = () => {
    return stepsForCustomClient.some(
      (ginStep) =>
        ginStep.completed_at === null || ginStep.completed_at === undefined,
    );
  };

  const getCompletionContent = () => {
    const stepIndex = getCurrentStepIndex();
    const adminName = currentStepAdmin?.first_name
      ? `${currentStepAdmin.first_name} ${currentStepAdmin.last_name}`
      : t('gin_steps|your_account_manager');

    // There are no gin steps for this grant
    if (ginForm?.number_of_steps === 0) {
      return {
        title: t('gin_steps|custom_client_no_gin_step_title'),
        message: t('gin_steps|custom_client_no_gin_step_msg', {
          admin_name: adminName,
        }),
      };
    }

    // Gin steps completed
    if (
      ginForm?.number_of_completed_steps === ginForm?.number_of_steps ||
      program?.program_grant_status === ProgramGrantStatus.Submitted
    ) {
      if (program?.program_grant_status === ProgramGrantStatus.Accepted) {
        return {
          title: t(
            'gin_steps_client|custom_client_gin_step_complete_accepted_title',
          ),
          message: t(
            'gin_steps_client|custom_client_gin_step_complete_accepted_msg',
            {
              provider: currentGrant?.grant_providers?.[0] ?? '',
              amount: dollarFormat(
                program?.program_targeted_amount ?? 0,
                selectedLocale,
                true,
              ),
              admin_name: adminName,
            },
          ),
        };
      } else if (
        program?.program_grant_status === ProgramGrantStatus.Submitted
      ) {
        return {
          title: t(
            'gin_steps_client|custom_client_gin_step_complete_submitted_waiting_title',
          ),
          message: t(
            'gin_steps_client|custom_client_gin_step_complete_submitted_waiting_msg',
            {
              provider: currentGrant?.grant_providers?.[0] ?? '',
            },
          ),
        };
      }
      return {
        title: t('gin_steps|custom_client_gin_step_complete_title'),
        message: t('gin_steps|custom_client_gin_step_complete_msg', {
          provider: currentGrant?.grant_providers?.[0] ?? '',
        }),
      };
    }
    // Client does not have tasks and the gin step is not completed
    if (
      stepsForCustomClient.length === 0 &&
      ginForm?.number_of_completed_steps !== ginForm?.number_of_steps
    ) {
      if (stepIndex === 1 && !!program?.program_client_requested_at) {
        return {
          title: t('gin_steps_client|custom_client_gin_step_not_started_title'),
          message: t(
            'gin_steps_client|custom_client_gin_step_not_complete_msg',
            {
              admin_name: adminName,
            },
          ),
        };
      }
      return {
        title: t('gin_steps|custom_client_gin_step_not_complete_title'),
        message: t('gin_steps|custom_client_gin_step_not_complete_msg', {
          admin_name: adminName,
          current_step: stepIndex,
          step_total: ginForm?.number_of_steps ?? '0',
          step_name:
            ginForm?.current_step?.translated_title ??
            ginForm?.current_step?.title_fr,
        }),
      };
    }

    // Client has tasks and they are not completed
    if (customClientHasTasksNotCompleted()) {
      return {
        title: t('gin_steps|custom_client_task_title'),
        message: t('gin_steps|custom_client_task_description'),
      };
    }

    // Client has tasks and they are completed
    return {
      title: t('gin_steps|custom_client_completed_tasks_title'),
      message: t('gin_steps|custom_client_completed_tasks_msg', {
        admin_name: adminName,
        current_step: getCurrentStepIndex(),
        step_total: ginForm?.number_of_steps ?? '0',
        step_name:
          ginForm?.current_step?.translated_title ??
          ginForm?.current_step?.title_fr,
      }),
    };
  };

  const { title, message } = getCompletionContent();

  useEffect(() => {
    const firstIncompleteIndex = stepsForCustomClient.findIndex(
      (step) =>
        !step.completed_at &&
        isGinStepAssignedToContact(step, contact?.contact_id),
    );

    setActiveStepIndex(firstIncompleteIndex >= 0 ? firstIncompleteIndex : null);
  }, [stepsForCustomClient, contact?.contact_id]);

  // Track completed step IDs to detect new completions
  const [completedStepIds, setCompletedStepIds] = useState<Set<string>>(
    new Set(
      stepsForCustomClient.filter((s) => s.completed_at).map((s) => s.step_id),
    ),
  );

  // Detect NEW completion and open next step if it's assigned to current user
  useEffect(() => {
    const currentCompleted = new Set(
      stepsForCustomClient.filter((s) => s.completed_at).map((s) => s.step_id),
    );

    // Find newly completed steps
    stepsForCustomClient.forEach((step, index) => {
      if (
        step.completed_at &&
        !completedStepIds.has(step.step_id) &&
        activeStepIndex === index
      ) {
        // This step JUST completed, check the next one
        const nextIndex = index + 1;
        if (nextIndex < stepsForCustomClient.length) {
          const nextStep = stepsForCustomClient[nextIndex];
          if (
            isGinStepAssignedToContact(nextStep, contact?.contact_id) &&
            !nextStep.completed_at
          ) {
            setActiveStepIndex(nextIndex);
          } else {
            setActiveStepIndex(null);
          }
        } else {
          setActiveStepIndex(null);
        }
      }
    });

    setCompletedStepIds(currentCompleted);
  }, [
    stepsForCustomClient,
    contact?.contact_id,
    activeStepIndex,
    completedStepIds,
  ]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        border: '1px solid',
        borderColor: theme.colors.grey_1,
        backgroundColor: theme.colors.white_1,
        gap: '16px',
      }}
    >
      {isLoading || !formFillingProps ? (
        <Loading />
      ) : (
        <GinStepsProvider
          viewMode={selectedLocale === 'en' ? 'client_en' : 'client_fr'}
          formState={'filling'}
          formFillingProps={formFillingProps}
          customRichTextParseOptions={getRichTextParseOptions}
          downloadAsset={handleDownloadAsset}
          stepsById={stepsById ?? {}}
          itemsById={itemsById ?? {}}
          pagesById={pagesById ?? {}}
          itemIdsByStepId={itemIdsByStepId}
          stepIdsByPageId={stepIdsByPageId}
          activeContactId={contact?.contact_id}
        >
          {contextHolder}
          <GinStepsCustomClientState>
            <Typography elementTheme="h6">{title}</Typography>
            <div style={{ textAlign: 'center' }} color={theme.colors.grey_2}>
              {message}
            </div>
          </GinStepsCustomClientState>
          {(!program?.program_grant_status ||
            !excludedStatuses.includes(program?.program_grant_status)) &&
            stepsForCustomClient.map((step, index) => (
              <GinStepSteps
                key={index}
                currentStep={step}
                isActive={activeStepIndex === index}
                onToggle={() =>
                  setActiveStepIndex(activeStepIndex === index ? null : index)
                }
              />
            ))}

          <StepsManagerModal
            open={applicationStatusModal.open}
            onClose={closeApplicationStatusModal}
            onConfirm={applicationStatusModal.onConfirm}
            neededProperties={applicationStatusModal.neededProperties}
          />
        </GinStepsProvider>
      )}
    </div>
  );
};

export default GinStepsCustomClient;
